import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { samplePages } from '../data/content';
export function LookInside() {
  const trackRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(samplePages.length - 1, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let minDistance = Infinity;
        Array.from(track.children).forEach((child, index) => {
          const el = child as HTMLElement;
          const distance = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
          if (distance < minDistance) {
            minDistance = distance;
            closest = index;
          }
        });
        setActive(closest);
      });
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  // Keep the active tab visible when the tab row scrolls on small screens.
  useEffect(() => {
    const list = tabsRef.current;
    const tab = list?.children[active] as HTMLElement | undefined;
    if (!list || !tab) return;
    list.scrollTo({
      left: tab.offsetLeft - (list.clientWidth - tab.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [active]);
  const current = samplePages[active];
  return <section aria-roledescription="carrusel" aria-label="Páginas de muestra de INGLÉS LISTO" className="w-full overflow-hidden bg-stone-900 py-20 text-white sm:py-28" data-id="element-132" data-mp="13oldy6kkia12">
      <div className="mx-auto max-w-3xl px-5 text-center" data-id="element-133" data-mp="1df6kqhaeakv2">
        <h2 className="font-serif text-[2rem] font-black leading-[1.08] tracking-tight sm:text-5xl" data-id="element-134" data-mp="1hgrsjo82o8ye">
          Mira lo que Hay <span className="italic text-red-500" data-id="element-135" data-mp="kr0z7sbioru6">Dentro.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/60 sm:text-base" data-id="element-136" data-mp="pokcoq0e50l2">
          Páginas reales del pack, listas para utilizar, imprimir y llevar a tus
          próximas clases.
        </p>

        <div ref={tabsRef} role="tablist" aria-label="Elige una página" className="relative mx-auto mt-8 flex w-fit max-w-full gap-1 overflow-x-auto rounded-full bg-white/10 p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-137" data-mp="yqtmterqyrxx">
          {samplePages.map((page, index) => {
          const isActive = index === active;
          return <button key={page.level} type="button" role="tab" aria-selected={isActive} onClick={() => scrollToIndex(index)} className={`shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-xs font-extrabold tracking-wide transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 sm:px-5 sm:text-sm ${isActive ? 'bg-red-600 text-white' : 'text-white/60 hover:text-white'}`} data-id="element-138" data-mp="2fassx4xcsvdl" data-mpi={index}>
                {page.level}
              </button>;
        })}
        </div>
      </div>

      <div className="relative mt-12" data-id="element-139" data-mp="3lx6facwe29ux">
        <div ref={trackRef} className="flex snap-x snap-mandatory gap-5 overflow-x-auto px-[11%] pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-8 sm:px-[29%] lg:px-[35%]" data-id="element-140" data-mp="3lw3p676w4bg2">
          {samplePages.map((page, index) => {
          const isActive = index === active;
          return <figure key={page.level} role="group" aria-roledescription="diapositiva" aria-label={`${index + 1} de ${samplePages.length}: ${page.title}`} className="w-[78%] shrink-0 snap-center sm:w-[42%] lg:w-[30%]" data-id="element-141" data-mp="3fznxu18q7c9b" data-mpi={index}>
                <button type="button" onClick={() => scrollToIndex(index)} tabIndex={isActive ? -1 : 0} aria-label={`Mostrar página de ${page.caption}`} className={`block w-full overflow-hidden rounded-2xl bg-white p-2 shadow-xl transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] focus:outline-none focus-visible:ring-4 focus-visible:ring-red-500/60 ${isActive ? 'scale-100 opacity-100' : 'scale-[0.92] opacity-40'}`} data-id="element-142" data-mp="3b136krbgc2x0">
                  <img src={page.image} alt={`Página de muestra (${page.caption}): ${page.title}`} className="aspect-[4/5] w-full rounded-xl object-cover object-top" loading="lazy" draggable={false} data-id="element-143" data-mp="23pgoel92tqxc" />
                </button>
              </figure>;
        })}
        </div>

        <button type="button" onClick={() => scrollToIndex(active - 1)} disabled={active === 0} aria-label="Página anterior" className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-red-600 text-white shadow-xl transition-[opacity,transform,background-color] duration-150 hover:scale-105 hover:bg-red-700 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-30 sm:left-4 sm:h-12 sm:w-12 lg:left-[calc(35%-5rem)]" data-id="element-144" data-mp="2mmi00ox4ypa9">
          <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-145" data-mp="280hgi9v5uiju" />
        </button>
        <button type="button" onClick={() => scrollToIndex(active + 1)} disabled={active === samplePages.length - 1} aria-label="Página siguiente" className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-red-600 text-white shadow-xl transition-[opacity,transform,background-color] duration-150 hover:scale-105 hover:bg-red-700 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-30 sm:right-4 sm:h-12 sm:w-12 lg:right-[calc(35%-5rem)]" data-id="element-146" data-mp="2c0hslnusehnq">
          <ChevronRightIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-147" data-mp="11dc2211qlogz" />
        </button>
      </div>

      <div className="mx-auto mt-8 max-w-md px-5 text-center" aria-live="polite" data-id="element-148" data-mp="xtbnlg00btp2">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-red-500" data-id="element-149" data-mp="xvepaxaivb9m">
          {current.caption}
        </p>
        <h3 className="mt-2 font-serif text-xl font-bold sm:text-2xl" data-id="element-150" data-mp="o38ulfsvyfqq">
          {current.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-white/60" data-id="element-151" data-mp="k2gkylg3q8tx">
          {current.text}
        </p>

        <div className="mt-6 flex items-center justify-center gap-2 sm:hidden" data-id="element-152" data-mp="1gsfx0pvgnph9">
          {samplePages.map((page, index) => <span key={page.level} aria-hidden="true" className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ${index === active ? 'w-6 bg-red-500' : 'w-1.5 bg-white/30'}`} data-id="element-153" data-mp="1btmeuk58baar" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}